import React from 'react'
import { pathOr } from 'ramda'
import { Segment, FormField, CommissionSplit, CurrencyField, PercentageField, Select } from 'components'
import { commissionUndertakings, leaseTypes } from 'modules/api/portfolio/constants'
import styles from './CommissionSegment.module.scss'
import { $TSFixMe } from 'types/ts-migrate'
import { ErrorPosition } from 'views/components/atoms/TextField/text-field.types'

type LeaseType = typeof leaseTypes[keyof typeof leaseTypes]
type CommissionUndertaking = typeof commissionUndertakings[keyof typeof commissionUndertakings]

interface CommissionAmount {
  value: $TSFixMe
  vatable: boolean
}

interface CommissionSplitValue {
  agent: {
    label: string
    value: string
  }
  splitPercentage: number
  isPrimary?: boolean
}

interface CommissionSegmentValues {
  type: CommissionUndertaking
  monthlyRentAmount: number | string
  netAmount: CommissionAmount
  percentage: CommissionAmount
  splits: CommissionSplitValue[]
  [key: string]: $TSFixMe
}
interface CommissionSegmentProps {
  name: string
  label: string
  leaseType: LeaseType
  leaseTermInMonths?: number
  rentAmount: number
  values: CommissionSegmentValues
  defaultResultSets?: $TSFixMe[][]
  setFieldTouched: $TSFixMe
  setFieldValue: $TSFixMe
  handleBlur: $TSFixMe
  handleChange: $TSFixMe
  getFieldError: (name: string) => string | undefined
  errors: $TSFixMe
}

const CommissionSegment = ({
  name,
  label,
  leaseType,
  leaseTermInMonths,
  rentAmount,
  values,
  defaultResultSets,
  setFieldTouched,
  setFieldValue,
  handleBlur,
  handleChange,
  getFieldError,
  errors,
}: CommissionSegmentProps) => {
  const typeOptions = [
    {
      value: commissionUndertakings.variable,
      label: 'Percentage',
    },
    {
      value: commissionUndertakings.fixed,
      label: 'Fixed amount',
    },
  ]

  return (
    <Segment.Group className={styles.root}>
      <Segment.Group horizontal>
        {/* @ts-expect-error */}
        <Segment style={{ width: '40%' }}>
          <h3 style={{ fontSize: '20px' }}>{label}</h3>
        </Segment>
        <Segment>
          <Select
            noOutline={true}
            style={{ marginTop: '10px' }}
            name={`${name}.type`}
            error={getFieldError(`${name}.type`)}
            value={typeOptions.find(option => option.value === values.type)}
            onChange={(data: $TSFixMe) => {
              setFieldValue(`${name}.type`, data.value)
              if (rentAmount && data.value !== values.type) {
                if (data.value === commissionUndertakings.fixed && values?.percentage?.value) {
                  const netAmount = rentAmount * (values.percentage.value / 100)
                  setFieldValue(`${name}.netAmount.value`, netAmount)
                } else if (data.value === commissionUndertakings.variable) {
                  const percentage = (values.netAmount.value / rentAmount) * 100
                  setFieldValue(`${name}.percentage.value`, percentage)
                  setFieldValue(`${name}.percentage.vatable`, values[name]?.percentage?.vatable || false)
                }
              }
            }}
            onBlur={setFieldTouched}
            options={typeOptions}
          />
        </Segment>
      </Segment.Group>
      <Segment.Group horizontal>
        <Segment>
          <div className="rbn--row">
            <div className="rbn--col-md">
              <FormField>
                <CurrencyField
                  disabled
                  name={`${name}.rentAmount`}
                  label="Rental amount"
                  error={getFieldError(`${name}.rentAmount`)}
                  value={rentAmount}
                />
              </FormField>
            </div>
            <div className="rbn--col-md">
              <FormField>
                {/** @todo convert to VAT Input Control */}
                {values.type === commissionUndertakings.fixed ? (
                  <CurrencyField
                    includeVatControl
                    vatApplied={values?.netAmount?.vatable}
                    name={`${name}.netAmount`}
                    label="Commission amount"
                    error={getFieldError(`${name}.netAmount`)}
                    value={values?.netAmount?.value || 0}
                    onToggleChange={(vatApplied: boolean) => {
                      setFieldValue(`${name}.netAmount.vatable`, vatApplied)
                    }}
                    onChange={(e: $TSFixMe) => {
                      setFieldValue(`${name}.netAmount.value`, e.target.value)
                    }}
                  />
                ) : (
                  <PercentageField
                    includeVatControl
                    vatApplied={values?.percentage?.vatable}
                    name={`${name}.percentage`}
                    label={`Percentage ${values?.percentage?.vatable ? '(incl. VAT)' : ''}`}
                    amount={rentAmount}
                    error={getFieldError(`${name}.percentage`)}
                    onFocus={() => setFieldTouched(`${name}.percentage`)}
                    value={values?.percentage?.value || 0}
                    onChange={(e: $TSFixMe) => {
                      setFieldValue(`${name}.percentage.value`, parseFloat(e.floatValue))
                    }}
                    onToggleChange={(vatApplied: boolean, value: $TSFixMe) =>
                      setFieldValue(`${name}.percentage.vatable`, vatApplied)
                    }
                    onBlur={handleBlur}
                    errorPosition={ErrorPosition.bottom}
                  />
                )}
              </FormField>
            </div>
          </div>
        </Segment>
      </Segment.Group>
      {leaseType === leaseTypes.unmanaged && values.type === commissionUndertakings.variable && (
        <Segment.Group>
          <Segment>Calculated based on lease term of {leaseTermInMonths} months.</Segment>
        </Segment.Group>
      )}
      <Segment.Group>
        <Segment>
          <CommissionSplit
            feeName={name}
            values={values}
            leaseType={leaseType}
            leaseTermInMonths={leaseTermInMonths}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            errors={pathOr({}, [...name.split('.'), 'splits'], errors)}
            defaultResultSets={defaultResultSets}
            type={name}
          />
        </Segment>
      </Segment.Group>
    </Segment.Group>
  )
}

export default CommissionSegment
